/**
 * VoiceRecordingStrip — what the composer becomes while recording.
 *
 * The prototype (`docs/GardEn/src/screens/Chat.jsx`) has no microphone, so
 * per the repo's drift rule this invents no visual language: every token,
 * radius, hit target and transition below is lifted from the existing
 * attach / camera / send buttons in `MessageInput.tsx`.
 *
 * REDUCED MOTION IS HANDLED EXPLICITLY. A pulsing red dot is exactly the
 * animation that triggers vestibular symptoms, and `motion-reduce:` utilities
 * on a *transition* do not cover a keyframe *animation* — so the pulse is
 * gated on `motion-safe:` and reduced-motion users get a solid dot that still
 * clearly reads as "recording".
 *
 * THE TIMER IS NOT ANNOUNCED. A screen reader reading "0:01, 0:02, 0:03" is
 * unusable, so the visible clock is `aria-hidden` and a polite live region
 * announces only transitions (started / stopped / transcribing / ready),
 * which is what a listener actually needs.
 */
import { Icon } from "@garden/ui";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

export interface VoiceRecordingStripProps {
  /** Elapsed recording time in ms. */
  elapsedMs: number;
  /** Input level in [0,1] driving the meter. */
  level: number;
  /** Hard cap in ms — drives the countdown warning. */
  maxDurationMs: number;
  /** True while the clip is being transcribed (recording has stopped). */
  isTranscribing: boolean;
  onStop: () => void;
  onCancel: () => void;
}

/** Seconds remaining at which the timer turns to a warning colour. */
const WARN_AT_REMAINING_S = 10;

/** Discrete bars, so the meter reads as a level rather than a waveform. */
const METER_BARS = 5;

export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export function VoiceRecordingStrip({
  elapsedMs,
  level,
  maxDurationMs,
  isTranscribing,
  onStop,
  onCancel,
}: VoiceRecordingStripProps): ReactElement {
  const { t } = useTranslation("chat");
  const remainingS = Math.max(0, Math.ceil((maxDurationMs - elapsedMs) / 1000));
  const isWarning = !isTranscribing && remainingS <= WARN_AT_REMAINING_S;

  return (
    <div
      className="flex items-center gap-2 px-2 py-2"
      data-testid="voice-strip"
      // Escape is the universal "get me out of here"; a recording the user
      // cannot abort without a mouse is a trap.
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          onCancel();
        }
      }}
    >
      <button
        type="button"
        onClick={onCancel}
        aria-label={t("voice.cancelAria")}
        title={t("voice.cancel")}
        data-testid="voice-cancel"
        className="grid size-11 shrink-0 place-items-center rounded-[10px] text-[var(--g-ink-3)] transition-colors duration-150 hover:bg-[var(--g-primary-soft)] hover:text-primary active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] motion-reduce:transition-none"
      >
        <Icon name="x" size={19} />
      </button>

      <span
        aria-hidden="true"
        data-testid="voice-dot"
        className={[
          "size-2.5 shrink-0 rounded-full",
          isTranscribing ? "bg-[var(--g-ink-3)]" : "bg-[var(--g-danger,#dc2626)]",
          // motion-safe, not motion-reduce: a keyframe animation must be
          // opted INTO, or reduced-motion users get the pulse anyway.
          isTranscribing ? "" : "motion-safe:animate-pulse",
        ].join(" ")}
      />

      <span
        aria-hidden="true"
        data-testid="voice-timer"
        className={[
          "g-data min-w-[42px] text-[14px] tabular-nums",
          isWarning ? "text-[var(--g-danger,#dc2626)]" : "text-[var(--g-ink-2)]",
        ].join(" ")}
      >
        {formatElapsed(elapsedMs)}
      </span>

      {isTranscribing ? (
        <span className="flex-1 text-[14px] text-[var(--g-ink-2)]">
          {t("voice.transcribing")}
        </span>
      ) : (
        <div className="flex flex-1 items-center gap-2">
          <div aria-hidden="true" className="flex items-end gap-[3px]">
            {Array.from({ length: METER_BARS }, (_, index) => {
              // Each bar lights at its own threshold, so the meter fills
              // left-to-right instead of every bar twitching together.
              const lit = level >= (index + 1) / (METER_BARS + 1);
              return (
                <span
                  key={index}
                  data-testid="voice-meter-bar"
                  data-lit={lit ? "true" : "false"}
                  className={[
                    "w-[3px] rounded-full transition-[height,background-color] duration-100 motion-reduce:transition-none",
                    lit ? "h-3 bg-primary" : "h-1.5 bg-[var(--g-hairline)]",
                  ].join(" ")}
                />
              );
            })}
          </div>
          <span className="text-[14px] text-[var(--g-ink-2)]">
            {isWarning
              ? t("voice.limitWarning", { seconds: remainingS })
              : t("voice.recording")}
          </span>
        </div>
      )}

      <button
        type="button"
        onClick={onStop}
        disabled={isTranscribing}
        aria-label={t("voice.stopAria")}
        title={t("voice.stop")}
        data-testid="voice-stop"
        className="grid size-11 shrink-0 place-items-center rounded-[10px] bg-primary text-[var(--g-on-primary)] transition-[background-color,transform] duration-150 hover:brightness-110 active:scale-95 disabled:pointer-events-none disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--g-surface)] motion-reduce:transition-none"
      >
        <Icon name="check" size={18} stroke={2.2} />
      </button>
    </div>
  );
}

export default VoiceRecordingStrip;
